import { inject, Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { AlertReportService } from '../services/alert-report.service';
import { AlertReportTemplateActions } from './alert-report-template.actions';
import { ToastService } from '../../common/services/toast/toast.service';
import { ToastType } from '../../common/models';

// Tags so each "processing" toast is replaced by its own completion toast.
const GENERATE_TOAST_TAG = 'alert-report-generate';
const EXPORT_TOAST_TAG = 'alert-report-export';
// Passed as the duration for persistent toasts — ignored (they don't auto-dismiss), so the default applies.
const PERSIST_TOAST = true;

@Injectable()
export class AlertReportTemplateEffects {
  private actions$ = inject(Actions);
  private alertReportService = inject(AlertReportService);
  private toastService = inject(ToastService);

  loadTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.loadTemplates),
    switchMap(() =>
      this.alertReportService.getTemplates().pipe(
        map((templates) => AlertReportTemplateActions.loadTemplatesSuccess({ templates })),
        catchError((error) => of(AlertReportTemplateActions.loadTemplatesFailure({ error })))
      )
    )
  ));

  loadComponentTypes$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.loadComponentTypes),
    switchMap(() =>
      this.alertReportService.getComponentTypes().pipe(
        map((componentTypes) => AlertReportTemplateActions.loadComponentTypesSuccess({ componentTypes })),
        catchError((error) => of(AlertReportTemplateActions.loadComponentTypesFailure({ error })))
      )
    )
  ));

  createTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.createTemplate),
    switchMap(({ payload }) =>
      this.alertReportService.createTemplate(payload).pipe(
        map((template) => AlertReportTemplateActions.createTemplateSuccess({ template })),
        catchError((error) => of(AlertReportTemplateActions.createTemplateFailure({ error })))
      )
    )
  ));

  deleteTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.deleteTemplate),
    concatMap(({ templateId }) =>
      this.alertReportService.deleteTemplate(templateId).pipe(
        map(() => AlertReportTemplateActions.deleteTemplateSuccess({ templateId })),
        catchError((error) => of(AlertReportTemplateActions.deleteTemplateFailure({ error })))
      )
    )
  ));

  previewTemplate$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.previewTemplate),
    switchMap(({ templateId, payload }) =>
      this.alertReportService.getTemplatePreview(templateId, payload).pipe(
        takeUntil(this.actions$.pipe(ofType(AlertReportTemplateActions.resetPreview))),
        map((html) => AlertReportTemplateActions.previewTemplateSuccess({ html })),
        catchError((error) => of(AlertReportTemplateActions.previewTemplateFailure({ error })))
      )
    )
  ));

  notifyRecipients$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.notifyRecipients),
    switchMap(({ payload }) =>
      this.alertReportService.notifyRecipients(payload).pipe(
        map(() => AlertReportTemplateActions.notifyRecipientsSuccess({ recipientCount: payload.recipient_ids.length })),
        catchError((error) => of(AlertReportTemplateActions.notifyRecipientsFailure({ error })))
      )
    )
  ));

  notifyRecipientsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.notifyRecipientsSuccess),
    tap(({ recipientCount }) => this.toastService.showToast(`Report sent to ${recipientCount} recipient(s)`, ToastType.SUCCESS))
  ), { dispatch: false });

  notifyRecipientsFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.notifyRecipientsFailure),
    tap(() => this.toastService.showToast('Failed to send report to recipients. Please try again.', ToastType.ERROR))
  ), { dispatch: false });

  generateReport$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.generateReport),
    tap(() => this.toastService.showToast('Your report is being generated. This may take a moment.', ToastType.INFO, undefined, GENERATE_TOAST_TAG, PERSIST_TOAST)),
    switchMap(({ payload, exportPayload }) =>
      this.alertReportService.generateReport(payload).pipe(
        mergeMap((blob) => {
          this.downloadBlob(blob, `alert-report-${this.fileTimestamp()}.pdf`);
          this.toastService.showToast('Report downloaded.', ToastType.SUCCESS, undefined, GENERATE_TOAST_TAG);
          const actions: Action[] = [AlertReportTemplateActions.generateReportSuccess()];
          // Chain the CSV export here (not in the dialog) so it still runs if the dialog was closed.
          if (exportPayload) {
            actions.push(AlertReportTemplateActions.exportAlerts({ payload: exportPayload }));
          }
          return actions;
        }),
        catchError((error) => {
          this.toastService.showToast('Failed to generate report. Please try again.', ToastType.ERROR, undefined, GENERATE_TOAST_TAG);
          return of(AlertReportTemplateActions.generateReportFailure({ error }));
        })
      )
    )
  ));

  exportAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertReportTemplateActions.exportAlerts),
    tap(() => this.toastService.showToast('Your CSV export is being prepared. This may take a moment.', ToastType.INFO, undefined, EXPORT_TOAST_TAG, PERSIST_TOAST)),
    switchMap(({ payload }) =>
      this.alertReportService.exportAlerts(payload).pipe(
        map((blob) => {
          // The endpoint is CSV-only; the server sets its own Content-Disposition filename.
          this.downloadBlob(blob, `alert-export-${this.fileTimestamp()}.csv`);
          this.toastService.showToast('CSV exported.', ToastType.SUCCESS, undefined, EXPORT_TOAST_TAG);
          return AlertReportTemplateActions.exportAlertsSuccess();
        }),
        catchError((error) => {
          this.toastService.showToast('Failed to export CSV. Please try again.', ToastType.ERROR, undefined, EXPORT_TOAST_TAG);
          return of(AlertReportTemplateActions.exportAlertsFailure({ error }));
        })
      )
    )
  ));

  private fileTimestamp(): string {
    return new Date().toISOString().replace(/[:.]/g, '-');
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      anchor.remove();
    }, 0);
  }
}
